/* eslint-disable i18next/no-literal-string --
 * Catalog identifiers, not UI copy. The list names mirror
 * `assets-api/src/taxonomy/standard-lists/standard-lists.catalog.ts` and are
 * matched BY NAME when seeding, so a translated name would stop a standard
 * field binding to the list that governs it.
 */
import { resolveBoundColumnPair } from '@inference.engine/taxonomy/web';
import type { TaxonomyField } from '@/entities/taxonomy';
import type { TaxonomyListSummary } from '@/entities/taxonomy-list';

/**
 * Which Standard List each standard Field draws its vocabulary from.
 *
 * These two catalogs ship the SAME vocabularies — the fields carried them
 * inline (copied into every schema, so a typo had to be fixed N times) before
 * Lists existed. Mapping them here makes the field a live reference to the
 * governed list instead, which is the whole reason Lists exist.
 *
 * Keys are `STANDARD_FIELD_LIBRARY[].key`. Values name the backend catalog
 * entry in `assets-api/src/taxonomy/standard-lists/standard-lists.catalog.ts`,
 * which carries the mirror of this mapping (`governsFields`) for server-side
 * backfill — change both together.
 *
 * `listName` is carried alongside `listKey` because seeding SKIPS a catalog
 * entry when the org already has a list of that name, so a hand-made "Regions"
 * list never gets the `regions` key — but it is still the org's regions
 * vocabulary and is what a bound field should point at.
 *
 * Fields absent from this map (free-text and date ones like Creative Concept or
 * Flight Start Date) have no vocabulary to govern and behave as before — hence
 * the `| undefined` in the value type, without which a lookup types as a hit and
 * every guard below reads as dead code.
 */
export const STANDARD_FIELD_LISTS: Readonly<
  Record<string, { listKey: string; listName: string } | undefined>
> = {
  region: { listKey: 'regions', listName: 'Regions' },
  country: { listKey: 'iso-countries', listName: 'Countries' },
  language: { listKey: 'iso-languages', listName: 'Languages' },
  channel: { listKey: 'channels', listName: 'Channels' },
  platform: { listKey: 'platforms', listName: 'Platforms' },
  'buy-type': { listKey: 'buy-types', listName: 'Buy Types' },
  'buy-method': { listKey: 'buy-methods', listName: 'Buy Methods' },
  device: { listKey: 'devices', listName: 'Devices' },
  objective: { listKey: 'objectives', listName: 'Objectives' },
  'funnel-stage': { listKey: 'funnel-stages', listName: 'Funnel Stages' },
  'funding-source': { listKey: 'funding-sources', listName: 'Funding Sources' },
  'creative-type': { listKey: 'creative-types', listName: 'Creative Types' },
  'ad-format': { listKey: 'ad-formats', listName: 'Ad Formats' },
  placement: { listKey: 'placements', listName: 'Placements' },
  'ad-type': { listKey: 'ad-types', listName: 'Ad Types' },
  audience: { listKey: 'audiences', listName: 'Audiences' },
  cta: { listKey: 'ctas', listName: 'Calls to Action' },
  version: { listKey: 'versions', listName: 'Versions' },
};

/**
 * The Standard List keys a set of standard fields needs seeded, deduped.
 *
 * Lives here rather than at the call site so the catalog knowledge stays in the
 * module that owns the mapping. Fields with no governed vocabulary (free-text,
 * date) contribute nothing, so a selection of only those yields an empty array
 * and needs no seed at all.
 */
export function standardListKeysFor(fieldKeys: string[]): string[] {
  const keys = new Set<string>();
  for (const key of fieldKeys) {
    const mapping = STANDARD_FIELD_LISTS[key];
    if (mapping) keys.add(mapping.listKey);
  }
  return [...keys];
}

/**
 * The org's list governing a standard field, or undefined when it hasn't been
 * seeded. Matches on catalog key first, then on name — see the note on
 * `STANDARD_FIELD_LISTS` for why a hand-made list has no key.
 */
export function findStandardList(
  fieldKey: string,
  lists: TaxonomyListSummary[],
): TaxonomyListSummary | undefined {
  const mapping = STANDARD_FIELD_LISTS[fieldKey];
  if (!mapping) return undefined;
  return (
    lists.find((l) => l.libraryKey === mapping.listKey) ??
    lists.find((l) => l.name.trim().toLowerCase() === mapping.listName.toLowerCase())
  );
}

/**
 * Builds the `listRef` for a standard field, or undefined when the org hasn't
 * seeded that list yet.
 *
 * Degrading to undefined is deliberate: the field then keeps the inline values
 * it has always shipped with, so adding standard fields works before standard
 * lists are seeded. It just isn't governed until they are.
 *
 * `listVersionId: null` means FOLLOW the list, i.e. read its latest PUBLISHED
 * version — publishing the schema is what pins one (see
 * `TaxonomyService.pinListBindings`).
 *
 * Stays SYNCHRONOUS, which is why the column ids come off the index summary
 * rather than a per-list detail fetch: `TaxonomyFields` calls this in a loop
 * while seeding N library fields, and up to 18 catalog fields map to a list.
 * The summary carries `boundColumns` — the columns of the version a following
 * binding actually reads — so no request is needed to get the right ones.
 */
export function standardFieldListRef(
  fieldKey: string,
  lists: TaxonomyListSummary[],
): NonNullable<TaxonomyField['listRef']> | undefined {
  const list = findStandardList(fieldKey, lists);
  // `boundColumns`, not `columns`: the binding reads the latest PUBLISHED
  // version, while `columns` is the head's and may name columns a draft renamed.
  // Both columns are required — binding a half-resolved list renders blank cells.
  const pair = resolveBoundColumnPair(list?.boundColumns);
  if (!list || !pair) return undefined;

  return {
    listId: list.id,
    listVersionId: null,
    valueColumnId: pair.valueColumn.id,
    codeColumnId: pair.codeColumn.id,
  };
}
